import AdsErrorsFactory from '../Errors/factory.js'
import AdEntityLegacy from './AdEntityLegacy.js'
import {FinancingConditionEntity} from './FinancingConditionEntity.js'
import {ReservationEntity} from './ReservationEntity.js'
import SubscriptionAdEntity from './SubscriptionAdEntity.js'

export default class AdsEntitiesFactory {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Array} deps.availableColors
   * @param {Number} deps.priceAverageIndicator
   * @param {Number} deps.category1Id
   * @param {Number} deps.bodyTypeId
   * @param {String} deps.color
   * @param {Number} deps.colorId
   * @param {String} deps.creationDate
   * @param {String} deps.description
   * @param {String} deps.environmentalLabel
   * @param {String} deps.externalAdId
   * @param {Object} deps.favoriteData
   * @param {Number} deps.financedPrice
   * @param {String} deps.fuelType
   * @param {Number} deps.fuelTypeId
   * @param {Boolean} deps.hasDiscount
   * @param {Boolean} deps.hasOfficialWarranty
   * @param {Boolean} deps.hasStock
   * @param {Boolean} deps.hasWarranty
   * @param {Number} deps.hp
   * @param {Number} deps.id
   * @param {String} deps.img
   * @param {Boolean} deps.includesTaxes
   * @param {Boolean} deps.isCertified
   * @param {Boolean} deps.isFavorite
   * @param {Boolean} deps.isFinanced
   * @param {Boolean} deps.isProfessional
   * @param {Boolean} deps.isTop - {motos-only param for ad visibility}
   * @param {Boolean} deps.isUrgent - {motos-only param for ad visibility}
   * @param {Number} deps.km
   * @param {Number} deps.location
   * @param {String} deps.make
   * @param {Number} deps.makeId
   * @param {String} deps.model
   * @param {Number} deps.modelId
   * @param {Number} deps.monthlyFee
   * @param {Number} deps.monthlySubscriptionFee
   * @param {Object} deps.offerType
   * @param {String} deps.phone
   * @param {Array} deps.photos
   * @param {Number} deps.price
   * @param {Object} deps.priceDropData
   * @param {Number} deps.provinceId
   * @param {String} deps.publicationDate
   * @param {Number} deps.priceRankIndicator
   * @param {Number} deps.specificFuelTypeId
   * @param {Number} deps.taxTypeId
   * @param {String} deps.title
   * @param {Number} deps.transmissionType
   * @param {Number} deps.transmissionTypeId
   * @param {Number} deps.traction
   * @param {String} deps.url
   * @param {Number} deps.warrantyId
   * @param {Number} deps.warrantyMonths
   * @param {String} deps.warrantyText
   * @param {Number} deps.year
   * @return {AdEntity}
   */
  static adEntity = ({
    availableColors,
    priceAverageIndicator,
    bodyTypeId,
    category1Id,
    color,
    colorId,
    commitmentMonthlyOptions,
    creationDate,
    description,
    environmentalLabel,
    externalAdId,
    favoriteData,
    financedPrice,
    financingInfo,
    fuelType,
    fuelTypeId,
    hasDiscount,
    hasNoCommitmentOption,
    hasOfficialWarranty,
    hasOnlineFinancing,
    hasReservation,
    hasStock,
    hasWarranty,
    hidePhone,
    hp,
    id,
    img,
    includesTaxes,
    isCertified,
    isFavorite,
    isFinanced,
    isProfessional,
    isTop,
    isTradable,
    isUrgent,
    isSubscription,
    km,
    kmIncluded,
    kmIncludedPeriod,
    location,
    make,
    makeId,
    minimumMonthlyFee,
    model,
    modelId,
    monthlyFee,
    monthlySubscriptionCommitment,
    monthlySubscriptionFee,
    offer,
    offerType,
    pack,
    phone,
    photos,
    plateRegistrationDate,
    price,
    priceDrop,
    priceDropData,
    province,
    provinceId,
    publicationDate,
    priceRankIndicator,
    seller,
    specificFuelTypeId,
    subscriptionBusinessAdInfo,
    subscriptionPriceOptions,
    subscriptionVehicleState,
    targetBuyer,
    taxTypeId,
    title,
    transmissionType,
    transmissionTypeId,
    traction,
    url,
    version,
    versionId,
    videos = [],
    warrantyId,
    warrantyMonths,
    warrantyText,
    year
  }) => {
    return AdEntityLegacy.create({
      availableColors,
      priceAverageIndicator,
      bodyTypeId,
      category1Id,
      color,
      colorId,
      commitmentMonthlyOptions,
      creationDate,
      description,
      environmentalLabel,
      externalAdId,
      favoriteData,
      financedPrice,
      financingInfo,
      fuelType,
      fuelTypeId,
      hasDiscount,
      hasNoCommitmentOption,
      hasOfficialWarranty,
      hasOnlineFinancing,
      hasReservation,
      hasStock,
      hasWarranty,
      hidePhone,
      hp,
      id,
      img,
      includesTaxes,
      isCertified,
      isFavorite,
      isFinanced,
      isProfessional,
      isTop,
      isTradable,
      isSubscription,
      isUrgent,
      km,
      kmIncluded,
      kmIncludedPeriod,
      location,
      make,
      makeId,
      minimumMonthlyFee,
      model,
      modelId,
      monthlyFee,
      monthlySubscriptionCommitment,
      monthlySubscriptionFee,
      offer,
      offerType,
      pack,
      phone,
      photos,
      plateRegistrationDate,
      price,
      priceDrop,
      priceDropData,
      province,
      provinceId,
      publicationDate,
      priceRankIndicator,
      seller,
      specificFuelTypeId,
      subscriptionBusinessAdInfo,
      subscriptionPriceOptions,
      subscriptionVehicleState,
      targetBuyer,
      taxTypeId,
      title,
      transmissionType,
      transmissionTypeId,
      traction,
      url,
      version,
      versionId,
      videos,
      warrantyId,
      warrantyMonths,
      warrantyText,
      year
    })
  }

  /**
   * @method
   * @param {Array} commitmentMonthlyOptions
   * @param {String} description
   * @param {String} fuelTypeId
   * @param {Boolean} hasNoCommitmentOption
   * @param {Number} id
   * @param {String} imgUrl
   * @param {String} make
   * @param {String} model
   * @param {Number} minimumMonthlyFee
   * @param {String} segment
   * @param {String} title
   * @param {String} transmissionTypeId
   * @param {String} url
   * @param {String} version
   */
  static subscriptionAdEntity = ({
    commitmentMonthlyOptions,
    description,
    specificFuelTypeId,
    hasNoCommitmentOption,
    id,
    imgUrl,
    make,
    model,
    minimumMonthlyFee,
    partnerName,
    powerHp,
    segment,
    title,
    transmissionTypeId,
    url,
    version,
    year
  }) =>
    new SubscriptionAdEntity({
      commitmentMonthlyOptions,
      description,
      specificFuelTypeId,
      hasNoCommitmentOption,
      id,
      imgUrl,
      make,
      model,
      minimumMonthlyFee,
      partnerName,
      powerHp,
      segment,
      title,
      transmissionTypeId,
      url,
      version,
      year
    })

  static financingConditionEntity = ({
    amountToFinance,
    availableTerm,
    availableTerms,
    capital,
    casualtyInsuranceAmount,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareId,
    fareInsuranceId,
    financedPrice,
    fee,
    firstFee,
    hasSpecificLegalConditions,
    id,
    insuranceCoverages,
    insuranceId,
    insuranceName,
    insuranceRate,
    isOpeningExpensesFinanced,
    lender,
    lenderLegalTextId,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    termsUrl,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      amountToFinance,
      availableTerm,
      availableTerms,
      capital,
      casualtyInsuranceAmount,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareId,
      fareInsuranceId,
      financedPrice,
      fee,
      firstFee,
      hasSpecificLegalConditions,
      id,
      insuranceCoverages,
      insuranceId,
      insuranceName,
      insuranceRate,
      isOpeningExpensesFinanced,
      lender,
      lenderLegalTextId,
      licenseWithdrawalInsuranceAmount,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      termsUrl,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static bmwBankConditionEntity = ({
    amountToFinance,
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareId,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount,
    dateOfferEnd
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      amountToFinance,
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareId,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount,
      dateOfferEnd
    })
  }

  static cofidisConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    casualtyInsuranceAmount,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    entry,
    fareInsuranceId,
    dateSignup,
    financedPrice,
    fee,
    firstFee,
    id,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    insuranceId,
    lender,
    lenderLegalTextId,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      firstFee,
      hasSpecificLegalConditions: false,
      id,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      insuranceId,
      lender,
      lenderLegalTextId,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static bbvaConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static santanderConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    casualtyInsuranceAmount,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    entry,
    fareInsuranceId,
    dateSignup,
    financedPrice,
    fee,
    firstFee,
    id,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    insuranceCoverages,
    insuranceId,
    insuranceName,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      casualtyInsuranceAmount,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      entry,
      fareInsuranceId,
      dateSignup,
      financedPrice,
      fee,
      firstFee,
      hasSpecificLegalConditions: true,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      id,
      insuranceCoverages,
      insuranceId,
      insuranceName,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static santanderPriConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    casualtyInsuranceAmount,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    fareInsuranceId,
    dateSignup,
    financedPrice,
    fee,
    firstFee,
    id,
    insuranceId,
    isOpeningExpensesFinanced,
    lender,
    lenderLegalTextId,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      casualtyInsuranceAmount,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      fareInsuranceId,
      dateSignup,
      financedPrice,
      fee,
      firstFee,
      hasSpecificLegalConditions: true,
      id,
      insuranceId,
      isOpeningExpensesFinanced,
      lender,
      lenderLegalTextId,
      licenseWithdrawalInsuranceAmount,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static caixaConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      hasSpecificLegalConditions: true,
      id,
      insuranceId,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      insuranceRate,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static lendrockConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static mobilizeConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static stellantisConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static vwfsConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    lender,
    lenderLegalTextId,
    licenseWithdrawalInsuranceAmount,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount,
    dateOfferEnd
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      lender,
      lenderLegalTextId,
      licenseWithdrawalInsuranceAmount,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount,
      dateOfferEnd
    })
  }

  static confiaConditionEntity = ({
    availableTerm,
    availableTerms,
    capital,
    coefficient,
    commission,
    commissionEur,
    computedPrice,
    dateFirstInstallment,
    dateSignup,
    entry,
    fareInsuranceId,
    fee,
    financedPrice,
    firstFee,
    id,
    insuranceId,
    insuranceRate,
    isOpeningExpensesFinanced,
    licenseWithdrawalInsuranceAmount,
    lender,
    lenderLegalTextId,
    lifeInsuranceAmount,
    loanRequestAvailable,
    maxEntry,
    maxEntryToGetDiscount,
    minEntry,
    openingExpenses,
    openingPercentage,
    selectedOption,
    tae,
    terms,
    tin,
    totalAmountDue,
    totalInsuranceAmount,
    totalInterest,
    totalLoanAmount,
    totalLoanCost,
    totalPriceTerms,
    totalTermsAmount
  }) => {
    FinancingConditionEntity.validateLegalTextId({
      lender,
      lenderLegalTextId,
      validateError: AdsErrorsFactory.getFinancingDataAdsError
    })
    return new FinancingConditionEntity({
      availableTerm,
      availableTerms,
      capital,
      coefficient,
      commission,
      commissionEur,
      computedPrice,
      dateFirstInstallment,
      dateSignup,
      entry,
      fareInsuranceId,
      fee,
      financedPrice,
      firstFee,
      id,
      insuranceId,
      insuranceRate,
      isOpeningExpensesFinanced,
      licenseWithdrawalInsuranceAmount,
      lender,
      lenderLegalTextId,
      lifeInsuranceAmount,
      loanRequestAvailable,
      maxEntry,
      maxEntryToGetDiscount,
      minEntry,
      openingExpenses,
      openingPercentage,
      selectedOption,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      totalTermsAmount
    })
  }

  static reservationEntity = ({adId, adReservationStatus, reservationId}) =>
    new ReservationEntity({adId, adReservationStatus, reservationId})
}
